import { utils as commonUtils } from '@neo-one/utils';
import { common } from './common';
import { crypto } from './crypto';
import { InvalidContractParameterError } from './errors';
import { JSONHelper } from './JSONHelper';
import { utils } from './utils';
const toBufferBuffer = (contractParameter) => {
    let value;
    switch (contractParameter.type) {
        case 'Signature':
            value = JSONHelper.readBuffer(contractParameter.value);
            break;
        case 'Boolean':
            value = contractParameter.value ? Buffer.alloc(1, 1) : Buffer.alloc(1, 0);
            break;
        case 'Integer':
            value = utils.toSignedBuffer(contractParameter.value);
            break;
        case 'Address':
            value = common.uInt160ToBuffer(crypto.addressToScriptHash({
                addressVersion: common.NEO_ADDRESS_VERSION,
                address: contractParameter.value,
            }));
            break;
        case 'Hash256':
            value = common.uInt256ToBuffer(JSONHelper.readUInt256(contractParameter.value));
            break;
        case 'Buffer':
            value = JSONHelper.readBuffer(contractParameter.value);
            break;
        case 'PublicKey':
            value = common.ecPointToBuffer(JSONHelper.readECPoint(contractParameter.value));
            break;
        case 'String':
            value = Buffer.from(contractParameter.value, 'utf8');
            break;
        case 'Array':
        case 'Map':
            throw new InvalidContractParameterError(contractParameter, [
                'Signature',
                'Boolean',
                'Integer',
                'Address',
                'Hash256',
                'Buffer',
                'PublicKey',
                'String',
                'Void',
            ]);
        case 'InteropInterface':
            value = Buffer.alloc(0, 0);
            break;
        case 'Void':
            value = Buffer.alloc(0, 0);
            break;
        default:
            commonUtils.assertNever(contractParameter);
            throw new Error('For TS');
    }
    return value;
};
const toBuffer = (contractParameter) => toBufferBuffer(contractParameter).toString('hex');
const toBoolean = (contractParameter) => {
    if (contractParameter.type === 'Array') {
        return contractParameter.value.some(toBoolean);
    }
    return toBufferBuffer(contractParameter).some((value) => value !== 0);
};
const toString = (contractParameter) => {
    if (contractParameter.type === 'String') {
        return contractParameter.value;
    }
    if (contractParameter.type === 'Buffer') {
        return JSONHelper.readBuffer(contractParameter.value).toString('utf8');
    }
    throw new InvalidContractParameterError(contractParameter, ['String', 'Buffer']);
};
const toAddress = (contractParameter) => {
    if (contractParameter.type === 'Address') {
        return contractParameter.value;
    }
    if (contractParameter.type === 'Buffer') {
        return crypto.scriptHashToAddress({
            scriptHash: common.bufferToUInt160(JSONHelper.readBuffer(contractParameter.value)),
            addressVersion: common.NEO_ADDRESS_VERSION,
        });
    }
    throw new InvalidContractParameterError(contractParameter, ['Address', 'Buffer']);
};
const toHash256 = (contractParameter) => {
    if (contractParameter.type === 'Hash256') {
        return contractParameter.value;
    }
    if (contractParameter.type === 'Buffer') {
        return JSONHelper.writeUInt256(common.bufferToUInt256(JSONHelper.readBuffer(contractParameter.value)));
    }
    throw new InvalidContractParameterError(contractParameter, ['Hash256', 'Buffer']);
};
const toPublicKey = (contractParameter) => {
    if (contractParameter.type === 'PublicKey') {
        return contractParameter.value;
    }
    if (contractParameter.type === 'Buffer') {
        return common.ecPointToString(JSONHelper.readECPoint(contractParameter.value));
    }
    throw new InvalidContractParameterError(contractParameter, ['PublicKey', 'Buffer']);
};
const toInteger = (contractParameter, parameter) => {
    let value;
    if (contractParameter.type === 'Integer') {
        value = contractParameter.value;
    }
    else if (contractParameter.type === 'Buffer') {
        value = utils.fromSignedBuffer(JSONHelper.readBuffer(contractParameter.value));
    }
    else {
        throw new InvalidContractParameterError(contractParameter, ['Integer', 'Buffer']);
    }
    return common.fixedToDecimal(value, parameter.decimals);
};
const toSignature = (contractParameter) => {
    if (contractParameter.type === 'Signature') {
        return contractParameter.value;
    }
    if (contractParameter.type === 'Buffer' && contractParameter.value.length === 128) {
        return contractParameter.value;
    }
    throw new InvalidContractParameterError(contractParameter, ['Signature']);
};
const toArray = (contractParameter, parameter) => {
    if (contractParameter.type !== 'Array') {
        throw new InvalidContractParameterError(contractParameter, ['Array']);
    }
    const { value } = parameter;
    const converter = contractParameters[value.type];
    return contractParameter.value.map((val) => converter(val, value));
};
const toMap = (contractParameter, parameter) => {
    if (contractParameter.type !== 'Map') {
        throw new InvalidContractParameterError(contractParameter, ['Map']);
    }
    const { key, value } = parameter;
    const keyConverter = contractParameters[key.type];
    const valueConverter = contractParameters[value.type];
    return new Map(contractParameter.value.map((val) => [keyConverter(val[0], key), valueConverter(val[1], value)]));
};
const toObject = (contractParameter, parameter) => {
    if (contractParameter.type !== 'Map') {
        throw new InvalidContractParameterError(contractParameter, ['Map']);
    }
    return contractParameter.value.reduce((acc, [keyParam, val]) => {
        const key = toString(keyParam);
        const value = parameter.properties[key];
        if (value === undefined) {
            throw new InvalidContractParameterError(contractParameter, ['Map']);
        }
        const converter = contractParameters[value.type];
        return {
            ...acc,
            [key]: converter(val, value),
        };
    }, {});
};
const toInteropInterface = (_contractParameter) => undefined;
const toVoid = (_contractParameter) => undefined;
const toForwardValue = (contractParameter) => contractParameter;
function wrapNullable(func) {
    return (contractParameter) => {
        try {
            if (contractParameter.type === 'Buffer' && contractParameter.value.length === 0) {
                return undefined;
            }
            if (contractParameter.type === 'Void') {
                return undefined;
            }
            return func(contractParameter);
        }
        catch (error) {
            if (error.code === 'INVALID_CONTRACT_PARAMETER' || error.code === 'INVALID_CONTRACT_PARAMETER_TYPE_JSON') {
                throw error;
            }
            return undefined;
        }
    };
}
function wrapNullableABI(func) {
    return (contractParameter, parameter) => {
        try {
            if (contractParameter.type === 'Buffer' && contractParameter.value.length === 0) {
                return undefined;
            }
            return func(contractParameter, parameter);
        }
        catch (error) {
            if (error.code === 'INVALID_CONTRACT_PARAMETER' || error.code === 'INVALID_CONTRACT_PARAMETER_TYPE_JSON') {
                throw error;
            }
            return undefined;
        }
    };
}
const toStringNullable = wrapNullable(toString);
const toAddressNullable = wrapNullable(toAddress);
const toHash256Nullable = wrapNullable(toHash256);
const toPublicKeyNullable = wrapNullable(toPublicKey);
const toIntegerNullable = wrapNullableABI(toInteger);
const toBooleanNullable = wrapNullable(toBoolean);
const toSignatureNullable = wrapNullable(toSignature);
const toBufferNullable = wrapNullable(toBuffer);
const toArrayNullable = wrapNullableABI(toArray);
const toMapNullable = wrapNullableABI(toMap);
const toObjectNullable = wrapNullableABI(toObject);
const toInteropInterfaceNullable = wrapNullable(toInteropInterface);
const toVoidNullable = wrapNullable(toVoid);
const toForwardValueNullable = wrapNullable(toForwardValue);
export const contractParameters = {
    String: (contractParameter, parameter) => parameter.optional ? toStringNullable(contractParameter) : toString(contractParameter),
    Address: (contractParameter, parameter) => parameter.optional ? toAddressNullable(contractParameter) : toAddress(contractParameter),
    Hash256: (contractParameter, parameter) => parameter.optional ? toHash256Nullable(contractParameter) : toHash256(contractParameter),
    PublicKey: (contractParameter, parameter) => parameter.optional ? toPublicKeyNullable(contractParameter) : toPublicKey(contractParameter),
    Integer: (contractParameter, parameter) => parameter.optional ? toIntegerNullable(contractParameter, parameter) : toInteger(contractParameter, parameter),
    Boolean: (contractParameter, parameter) => parameter.optional ? toBooleanNullable(contractParameter) : toBoolean(contractParameter),
    Signature: (contractParameter, parameter) => parameter.optional ? toSignatureNullable(contractParameter) : toSignature(contractParameter),
    Buffer: (contractParameter, parameter) => parameter.optional ? toBufferNullable(contractParameter) : toBuffer(contractParameter),
    Array: (contractParameter, parameter) => parameter.optional ? toArrayNullable(contractParameter, parameter) : toArray(contractParameter, parameter),
    Map: (contractParameter, parameter) => parameter.optional ? toMapNullable(contractParameter, parameter) : toMap(contractParameter, parameter),
    Object: (contractParameter, parameter) => parameter.optional ? toObjectNullable(contractParameter, parameter) : toObject(contractParameter, parameter),
    Void: (contractParameter, parameter) => parameter.optional ? toVoidNullable(contractParameter) : toVoid(contractParameter),
    ForwardValue: (contractParameter, parameter) => parameter.optional ? toForwardValueNullable(contractParameter) : toForwardValue(contractParameter),
};
export const smartContractConverters = {
    toString,
    toStringNullable,
    toAddress,
    toAddressNullable,
    toHash256,
    toHash256Nullable,
    toPublicKey,
    toPublicKeyNullable,
    toInteger,
    toIntegerNullable,
    toBoolean,
    toBooleanNullable,
    toSignature,
    toSignatureNullable,
    toBuffer,
    toBufferNullable,
    toArray,
    toArrayNullable,
    toMap,
    toMapNullable,
    toObject,
    toObjectNullable,
    toInteropInterface,
    toInteropInterfaceNullable,
    toVoid,
    toVoidNullable,
    toForwardValue,
    toForwardValueNullable,
};

//# sourceMappingURL=contractParameters.js.map
